"use client";

import dynamic from "next/dynamic";

import { ErrorView } from "@/components/error-view";
import { Oriented, mapOrientedToName } from "@/data/constants/oriented";
import { useSymbolInfo } from "@/features/analysis/services/symbol-info";
import { OverviewOrientation } from "@/features/analysis/services/symbol-overview";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";

import { indicatorScoresMap } from "../data/indicators-data";
import { getRadarData, radarInfo, tickInfo } from "../data/radar-data";

type Props = {
  lang: Lang;
  symbol: string;
  overview: OverviewOrientation;
};

const RadarChart = dynamic(
  () => import("ui/analysis/radar-chart").then((mod) => mod.RadarChart),
  { ssr: false }
);

export const AIStrategyRadar = ({ lang, symbol, overview }: Props) => {
  const { t } = getTranslations(lang);
  const { data: symbolInfo, isLoading, isError } = useSymbolInfo(symbol);
  const radarTexts = mapOrientedToName({ t });
  const radarData = getRadarData({ data: symbolInfo, radarTexts });
  const focusValues = Object.keys(indicatorScoresMap[overview]).map(
    (indicator) => radarTexts[indicator as Oriented]
  );

  if (isLoading) return <ChartSkeletons className="my-auto px-10" />;
  if (isError) return <ErrorView>{t.error.serverError}</ErrorView>;

  return (
    <div className="flex h-full items-center justify-center">
      <div className="mx-auto aspect-[1.3] w-full max-w-[400px] overflow-hidden">
        <figure
          className="aspect-[1.2]"
          aria-label="radar chart with various aspects"
        >
          <RadarChart
            tickInfo={tickInfo}
            data={radarData}
            radarInfo={radarInfo}
            lang={lang}
            focusValues={focusValues}
          />
        </figure>
      </div>
    </div>
  );
};
